import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, KeyboardAvoidingView, Modal, Platform, ScrollView, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { Image } from 'expo-image';
import * as ImagePicker from 'expo-image-picker';
import * as DocumentPicker from 'expo-document-picker';
import { useForm, useWatch } from 'react-hook-form';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useToast } from 'react-native-toast-notifications';
import RenderHtml from 'react-native-render-html';
import dayjs from 'dayjs';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import { Icon } from '../../../../components/Icon/Icon';
import { Illustration } from '../../../../components/Illustration';
import Input from '../../../../components/Forms/Input';
import useFormSubmit from '../../../../hooks/useFormSubmit';
import TutorialView from '../../../../components/Tutorial/TutorialView';
import { useTutorialSeen } from '../../../../components/Tutorial/tutorials';
import api from '../../../../utils/api';

import { FooterButtons, TabsSelector } from '../GuestPasses/NewGuestPassModal';
import PassDatesStep from '../GuestPasses/NewGuessPass/PassDatesStep';

// SignatureInk does not work on web, so we render a placeholder instead
import type { SignatureInkHandle } from 'react-native-signature-ink';
const SignatureInk = Platform.OS !== 'web' ? require('react-native-signature-ink').SignatureInk : null;

const TUTORIAL = 'autorizacion-proveedor';


type AccessAuthorizationSettings = {
  enabled?: boolean;
  require_terms?: boolean;
  require_signature?: boolean;
  terms?: string | null;
  qr_mode?: 'none' | 'guard' | 'guard_totem';
  service_hours?: Record<string, { from: string; to: string } | null> | null;
  // Catálogo de servicios: vacío si la comunidad no lo usa (descripción libre, como antes)
  service_types?: { uuid: string; name: string; description?: string | null }[];
  description?: 'off' | 'optional' | 'required';
};


const SERVICE_DAYS = [
  { key: 'mon', label: 'Lun' }, { key: 'tue', label: 'Mar' }, { key: 'wed', label: 'Mié' },
  { key: 'thu', label: 'Jue' }, { key: 'fri', label: 'Vie' }, { key: 'sat', label: 'Sáb' }, { key: 'sun', label: 'Dom' },
];

/** Agrupa días consecutivos con el mismo horario: "Lun–Vie 08:00–18:00 · Sáb 08:00–14:00". */
function formatServiceHours(hours: AccessAuthorizationSettings['service_hours']) {
  if (!hours) return null;

  const groups: { from: string; to: string; range: string | null; days: string[] }[] = [];
  for (const d of SERVICE_DAYS) {
    const r = hours[d.key];
    const range = r?.from && r?.to ? `${r.from}–${r.to}` : null;
    const last = groups[groups.length - 1];
    if (last && last.range === range) last.days.push(d.label);
    else groups.push({ from: r?.from ?? '', to: r?.to ?? '', range, days: [d.label] });
  }

  return groups
    .filter(g => g.range)
    .map(g => `${g.days.length > 1 ? `${g.days[0]}–${g.days[g.days.length - 1]}` : g.days[0]} ${g.range}`)
    .join(' · ') || 'Sin horario de servicio';
}


type Attachment = { uuid: string; url: string; file_type?: string; name?: string };

async function uploadAttachment(communityId: string, file: { uri: string; name?: string | null; mimeType?: string | null }) {
  const name = file.name ?? file.uri.split('/').pop() ?? 'archivo';
  const type = file.mimeType ?? (name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg');

  const fd = new FormData();
  if (Platform.OS === 'web') {
    const blob = await (await fetch(file.uri)).blob();
    fd.append('image', blob, name);
  } else {
    // @ts-ignore RN file shape
    fd.append('image', { uri: file.uri, name, type });
  }

  const res = await api.post(`/communities/${communityId}/images`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data as Attachment;
}


/** Fotos o documentos de apoyo (permiso de obra, lista de trabajadores…). Opcional. */
function FilesStep({ communityId, files, setFiles, isLastStep, isSubmitting, onBackPressed, onNextPressed }: any) {
  const toast = useToast();
  const [uploading, setUploading] = useState(false);

  async function upload(file: any) {
    setUploading(true);
    try {
      const uploaded = await uploadAttachment(communityId, file);
      setFiles((prev: Attachment[]) => [...prev, uploaded]);
    } catch (err: any) {
      toast.show(err?.response?.data?.message ?? 'No se pudo subir el archivo', { type: 'warning' });
    } finally {
      setUploading(false);
    }
  }

  async function takePhoto() {
    const perm = await ImagePicker.requestCameraPermissionsAsync();
    if (!perm.granted) {
      toast.show('Necesitamos permiso de cámara', { type: 'warning' });
      return;
    }
    const result = await ImagePicker.launchCameraAsync({ quality: 0.7 });
    if (!result.canceled && result.assets?.[0]) await upload(result.assets[0]);
  }

  async function pickFile() {
    const result = await DocumentPicker.getDocumentAsync({ type: ['image/*', 'application/pdf'] });
    if (!result.canceled && result.assets?.[0]) await upload(result.assets[0]);
  }

  return (
    <>
      <View style={tw`flex-1`}>
        <Text style={tw`text-white text-center text-xl font-500 mb-2`}>Adjuntos</Text>
        <Text style={tw`text-white text-center mb-6`}>
          Opcional. Agrega fotos o documentos que ayuden a la administración a revisar tu solicitud, como un permiso de obra o la lista de trabajadores.
        </Text>

        {files.length > 0 && (
          <View style={tw`row flex-wrap gap-3 mb-6`}>
            {files.map((f: Attachment) => (
              <View key={f.uuid}>
                {f.file_type === 'pdf' ? (
                  <View style={tw`w-20 h-20 rounded-xl bg-white items-center justify-center`}>
                    <Text style={tw`text-gray-500 font-600`}>PDF</Text>
                  </View>
                ) : (
                  <Image source={f.url} style={tw`w-20 h-20 rounded-xl bg-white`} />
                )}
                <TouchableOpacity
                  onPress={() => setFiles((prev: Attachment[]) => prev.filter(p => p.uuid !== f.uuid))}
                  style={tw`absolute -top-2 -right-2 bg-white rounded-full border border-gray-200 w-6 h-6 items-center justify-center`}
                >
                  <Ionicons name='close' size={14} color='#374151' />
                </TouchableOpacity>
              </View>
            ))}
          </View>
        )}

        {uploading ? (
          <ActivityIndicator color='white' style={tw`my-6`} />
        ) : files.length < 10 && (
          <View style={tw`row gap-3`}>
            {Platform.OS !== 'web' && (
              <TouchableOpacity onPress={takePhoto} style={tw`flex-1 bg-white rounded-xl py-4 items-center`}>
                <Ionicons name='camera-outline' size={26} color={tw.color('primary')} />
                <Text style={tw`text-primary font-500 mt-1`}>Tomar foto</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity onPress={pickFile} style={tw`flex-1 bg-white rounded-xl py-4 items-center`}>
              <Ionicons name='document-attach-outline' size={26} color={tw.color('primary')} />
              <Text style={tw`text-primary font-500 mt-1`}>Elegir archivo</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      <FooterButtons
        onBackPressed={onBackPressed}
        onNextPressed={onNextPressed}
        nextDisabled={uploading}
        lastStep={isLastStep}
        isSubmitting={isSubmitting}
      />
    </>
  );
}


/** Catálogo de servicios permitidos: el residente elige uno antes de describir el servicio. */
function ServiceTypeStep({ control, setValue, types, onNextPressed, onCancelPressed }) {
  const selected = useWatch({ control, name: 'service_type_uuid' });

  return (
    <>
      <Text style={tw`text-white text-center text-xl font-500 mb-4`}>¿Qué tipo de servicio es?</Text>

      <ScrollView style={tw`flex-1`} contentContainerStyle={tw`gap-3 pb-2`}>
        {types.map((t) => {
          const active = selected === t.uuid;
          return (
            <TouchableOpacity
              key={t.uuid}
              onPress={() => setValue('service_type_uuid', t.uuid)}
              activeOpacity={0.8}
              style={tw.style(`row items-start bg-white rounded-xl px-4 py-3 border-2`, active ? `border-secondary` : `border-transparent`)}
            >
              <View style={tw.style(`w-5 h-5 rounded-full border-2 mr-3 mt-0.5 items-center justify-center`, active ? `border-secondary` : `border-gray-300`)}>
                {active && <View style={tw`w-2.5 h-2.5 rounded-full bg-secondary`} />}
              </View>
              <View style={tw`flex-1`}>
                <Text style={tw`text-secondary font-600 text-base`}>{t.name}</Text>
                {!!t.description && <Text style={tw`text-gray-500 text-sm mt-0.5`}>{t.description}</Text>}
              </View>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      <FooterButtons
        showCancelButton
        onCancelPressed={onCancelPressed}
        onNextPressed={onNextPressed}
        nextDisabled={!selected}
      />
    </>
  );
}


function ServiceStep({ control, setValue, descriptionMode, isFirstStep, onNextPressed, onCancelPressed, onBackPressed }) {
  const { description, access_type } = useWatch({ control });

  const isValid = (descriptionMode !== 'required' || !!description?.trim()) && !!access_type;

  return (
    <>
      <View style={tw`flex-1`}>
        {descriptionMode !== 'off' && (
          <>
            <Text style={tw`text-white text-center text-xl font-500 mb-6`}>
              Describe el servicio{descriptionMode === 'optional' ? ' (opcional)' : ''}
            </Text>

            <Input
              control={control}
              name='description'
              rules={{ required: descriptionMode === 'required' }}
              multiline
              numberOfLines={4}
              style={{ subcontainer: `bg-white min-h-[120px] rounded-xl border border-gray-300`, input: `text-base` }}
              placeholder='Ej: Mudanza con camión 3.5 ton para entrar a la casa por la puerta principal'
            />
          </>
        )}

        <Text style={tw.style(`text-white text-center text-xl font-500 mb-4`, descriptionMode !== 'off' && `mt-8`)}>Tipo de acceso</Text>

        <TabsSelector
          onChange={(value) => setValue('access_type', value)}
          values={[
            { key: 'vehicular', title: 'Vehicular' },
            { key: 'pedestrian', title: 'Peatonal' },
          ]}
          initialValue={access_type || 'vehicular'}
        />
      </View>

      <FooterButtons
        showCancelButton={isFirstStep}
        onCancelPressed={onCancelPressed}
        onBackPressed={onBackPressed}
        onNextPressed={onNextPressed}
        nextDisabled={!isValid}
      />
    </>
  );
}


function TermsStep({ control, setValue, terms, isLastStep, isSubmitting, onBackPressed, onNextPressed }) {
  const accepted = useWatch({ control, name: 'terms_accepted' });
  const { width } = useWindowDimensions();

  return (
    <>
      <View style={tw`flex-1`}>
        <Text style={tw`text-white text-center text-xl font-500 mb-6`}>Términos y condiciones</Text>

        <ScrollView style={tw`flex-1 bg-white rounded-xl border border-gray-300 mb-4`} contentContainerStyle={tw`px-4 py-4`}>
          {
            terms?.trim()
              ? <RenderHtml 
                  contentWidth={width - 56}
                  source={{ html: terms }} 
                  tagsStyles={{
                    p: tw`text-gray-700 mb-3`,
                    li: tw`text-gray-700 mb-2`,
                    h2: tw`text-gray-900 text-lg font-600 mb-3`,
                    b: tw`text-gray-900 font-600`,
                  }}
                />
              : <Text style={tw`text-gray-700 text-base leading-6`}>No hay términos configurados.</Text>
          }
        </ScrollView>

        <TouchableOpacity
          style={tw`row items-center bg-primary-100 rounded-xl px-4 py-4`}
          onPress={() => setValue('terms_accepted', !accepted)}
          activeOpacity={0.8}
        >
          <View style={tw.style(
            `h-6 w-6 rounded-md border-2 border-primary items-center justify-center mr-3`,
            accepted && `bg-primary`,
          )}>
            {accepted && <Ionicons name='checkmark' color={'#fff'} size={16} />}
          </View>

          <Text style={tw`text-primary-800 font-500 flex-1`}>He leído y acepto los términos y condiciones</Text>
        </TouchableOpacity>
      </View>

      <FooterButtons
        onBackPressed={onBackPressed}
        onNextPressed={onNextPressed}
        nextDisabled={!accepted}
        lastStep={isLastStep}
        isSubmitting={isSubmitting}
      />
    </>
  );
}


function SignatureStep({ signatureRef, isLastStep, isSubmitting, onBackPressed, onNextPressed }) {
  return (
    <>
      <View style={tw`flex-1`}>
        <Text style={tw`text-white text-center text-xl font-500 mb-2`}>Firma de autorización</Text>
        <Text style={tw`text-white text-center mb-4`}>Firma dentro del recuadro para autorizar el acceso</Text>

        <View style={tw`flex-1 bg-white rounded-xl border border-gray-300 overflow-hidden`}>
          {
            Platform.OS !== 'web'
              ? (
                <SignatureInk
                  ref={signatureRef}
                  style={{ flex: 1 }}
                  showBaseline
                  showToolbar
                  penColor='#111'
                  toolbarButtons={['clear']}
                />
              )
              : (
                <View style={tw`flex-1 items-center justify-center`}>
                  <Text style={tw`text-gray-400`}>La firma no está disponible en web</Text>
                </View>
              )
          }
        </View>
      </View>

      <FooterButtons
        onBackPressed={onBackPressed}
        onNextPressed={onNextPressed}
        lastStep={isLastStep}
        isSubmitting={isSubmitting}
      />
    </>
  );
}


export default function NewAccessAuthorizationModal({
  visible, onRequestClose, communityId, unitId, settings, onCreated,
}: {
  visible: boolean;
  onRequestClose: () => void;
  communityId: string;
  unitId: string;
  settings?: AccessAuthorizationSettings;
  onCreated: (auth: any) => void;
}) {
  const insets = useSafeAreaInsets();
  const toast = useToast();
  const signatureRef = useRef<SignatureInkHandle>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [files, setFiles] = useState<Attachment[]>([]);
  const [tutorialSeen, setTutorialSeen] = useTutorialSeen(TUTORIAL);
  const [showTutorial, setShowTutorial] = useState(false);
  const serviceHoursStr = formatServiceHours(settings?.service_hours);
  const serviceTypes = settings?.service_types ?? [];
  const hasCatalog = serviceTypes.length > 0;
  // Sin catálogo la descripción es lo único que describe el servicio: obligatoria
  const descriptionMode = hasCatalog ? (settings?.description ?? 'optional') : 'required';

  const { control, setValue, handleSubmit, reset } = useForm({
    defaultValues: {
      service_type_uuid: null as string | null,
      description: '',
      access_type: 'vehicular',
      periodicity: 'one_time',
      entry_date: new Date(),
      entry_date_limit: null as Date | null,
      terms_accepted: false,
      signature: null as string | null,
    },
    mode: 'onChange',
  });

  // Pasos dinámicos: los de términos y firma solo aparecen si la comunidad los exige
  const steps = useMemo(() => {
    const list: string[] = hasCatalog ? ['service_type'] : [];
    list.push('service', 'dates', 'files');
    if (settings?.require_terms) list.push('terms');
    if (settings?.require_signature) list.push('signature');
    return list;
  }, [hasCatalog, settings?.require_terms, settings?.require_signature]);

  const currentStep = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;

  useEffect(() => {
    if (visible) {
      reset();
      setValue('access_type', 'vehicular');
      setStepIndex(0);
      setFiles([]);
      // La primera vez el tutorial sale antes del formulario
      setShowTutorial(!tutorialSeen);
    }
  }, [visible]);

  // Se da por visto al salir del tutorial, aunque no se haya terminado: no se obliga a verlo completo
  function closeTutorial() {
    setTutorialSeen();
    setShowTutorial(false);
  }

  function close() {
    if (showTutorial) setTutorialSeen();
    onRequestClose();
  }

  const { submitForm, isSubmitting } = useFormSubmit({
    onSuccess: (auth) => onCreated(auth),
    onError: () => {},
    successToastMessage: 'Autorización creada',
    endpoint: `/communities/${communityId}/access-authorizations`,
  });

  function onSubmit(data) {
    const isRecurrent = data.periodicity === 'recurrent';
    const startDate = dayjs(data.entry_date).format('YYYY-MM-DD');
    const endDate = isRecurrent && data.entry_date_limit
      ? dayjs(data.entry_date_limit).format('YYYY-MM-DD')
      : null;

    submitForm({
      unit_uuid: unitId,
      ...(hasCatalog && { service_type_uuid: data.service_type_uuid }),
      description: descriptionMode === 'off' ? null : (data.description?.trim() || null),
      access_type: data.access_type,
      start_date: startDate,
      end_date: endDate,
      start_time: null,
      end_time: null,
      terms_accepted: !!data.terms_accepted,
      signature: data.signature ?? null,
      file_uids: files.map(f => f.uuid),
    });
  }

  async function goNext() {
    // Captura la firma (SVG) antes de salir del paso, ya que SignatureInk se desmonta al avanzar
    if (currentStep === 'signature') {
      const empty = await signatureRef.current?.isEmpty();
      if (empty) {
        toast.show('Por favor firma antes de continuar', { type: 'warning' });
        return;
      }

      const svg = await signatureRef.current?.toSvg();
      setValue('signature', svg ?? null);
    }

    if (isLastStep) {
      handleSubmit(onSubmit)();
    } else {
      setStepIndex((i) => i + 1);
    }
  }

  function goBack() {
    setStepIndex((i) => Math.max(0, i - 1));
  }

  return (
    <Modal
      onRequestClose={close}
      visible={visible}
      statusBarTranslucent
      animationType='slide'
    >
      {/* El tutorial lleva su propia ropa (fondo gris, encabezado y botón de salida distintos): con el
          turquesa y el encabezado del formulario se confundía con la pantalla simulada que muestra */}
      <View style={tw.style(`px-5 flex-1 pt-[${insets.top}px]`, showTutorial ? `bg-[#F3F2F3]` : `bg-primary`)}>
        {showTutorial && (
          <View style={tw`flex-1 pb-[${insets.bottom + 16}px]`}>
            <View style={tw`row justify-between items-center mt-5 mb-4`}>
              <View style={tw`flex-1 pr-3`}>
                <View style={tw`self-start bg-secondary/10 rounded-full px-2.5 py-0.5 mb-1`}>
                  <Text style={tw`text-xs font-600 text-secondary`}>TUTORIAL</Text>
                </View>
                <Text style={tw`text-lg font-600 text-gray-700`}>Cómo pedir una autorización</Text>
              </View>

              <TouchableOpacity style={tw`items-center justify-center mr-1`} onPress={close} accessibilityLabel='Cerrar'>
                <Ionicons name='close' size={28} color={tw.color('gray-500')} />
              </TouchableOpacity>
            </View>

            <TutorialView slug={TUTORIAL} style={tw`mb-4 border border-gray-200`} />

            <TouchableOpacity
              style={tw`row items-center justify-center rounded-full border-2 border-[#11554F] py-3.5`}
              onPress={closeTutorial}
            >
              <Text style={tw`text-base font-500 text-[#11554F] mr-1`}>Ir a mi solicitud</Text>
              <Ionicons name='chevron-forward' size={18} color='#11554F' />
            </TouchableOpacity>
          </View>
        )}

        {!showTutorial && (
          <View style={tw`row justify-between mb-8 mt-5`}>
            <View style={tw`row`}>
              <Illustration name='autorizacion' width={40} height={40} style={tw`mr-3`} />

              <Text style={tw`text-lg font-600 text-secondary`}>Nueva autorización</Text>
            </View>

            <View style={tw`row items-center`}>
              {/* En el paso de firma no: al desmontarse SignatureInk se pierde el trazo */}
              {currentStep !== 'signature' && (
                <TouchableOpacity
                  style={tw`items-center justify-center mr-4`}
                  onPress={() => setShowTutorial(true)}
                  accessibilityLabel='Ver tutorial'
                >
                  <Ionicons name='help-circle-outline' size={28} color={tw.color('secondary')} />
                </TouchableOpacity>
              )}

              <TouchableOpacity
                style={tw`items-center justify-center mr-2`}
                onPress={close}
              >
                <Icon name='close' size={26} color={tw.color('red')} />
              </TouchableOpacity>
            </View>
          </View>
        )}

        {!showTutorial && (
          <KeyboardAvoidingView
            style={tw`flex-1`}
            behavior='padding'
            contentContainerStyle={tw`flex-1`}
          >
            {currentStep === 'service_type' && (
              <ServiceTypeStep
                control={control}
                setValue={setValue}
                types={serviceTypes}
                onCancelPressed={onRequestClose}
                onNextPressed={goNext}
              />
            )}

            {currentStep === 'service' && (
              <ServiceStep
                control={control}
                setValue={setValue}
                descriptionMode={descriptionMode}
                isFirstStep={stepIndex === 0}
                onCancelPressed={onRequestClose}
                onBackPressed={goBack}
                onNextPressed={goNext}
              />
            )}

            {currentStep === 'dates' && !!serviceHoursStr && (
              <View style={tw`row items-center bg-white/15 rounded-xl px-3 py-2 mb-3`}>
                <Ionicons name='time-outline' size={18} color='white' />
                <Text style={tw`text-white text-sm ml-2 flex-1`}>Horario para proveedores: {serviceHoursStr}</Text>
              </View>
            )}

            {currentStep === 'dates' && (
              <PassDatesStep
                control={control}
                setValue={setValue}
                isLastStep={isLastStep}
                isSubmitting={isSubmitting}
                onBackPressed={goBack}
                onNextPressed={goNext}
              />
            )}

            {currentStep === 'files' && (
              <FilesStep
                communityId={communityId}
                files={files}
                setFiles={setFiles}
                isLastStep={isLastStep}
                isSubmitting={isSubmitting}
                onBackPressed={goBack}
                onNextPressed={goNext}
              />
            )}

            {currentStep === 'terms' && (
              <TermsStep
                control={control}
                setValue={setValue}
                terms={settings?.terms}
                isLastStep={isLastStep}
                isSubmitting={isSubmitting}
                onBackPressed={goBack}
                onNextPressed={goNext}
              />
            )}

            {currentStep === 'signature' && (
              <SignatureStep
                signatureRef={signatureRef}
                isLastStep={isLastStep}
                isSubmitting={isSubmitting}
                onBackPressed={goBack}
                onNextPressed={goNext}
              />
            )}
          </KeyboardAvoidingView>
        )}
      </View>
    </Modal>
  );
}
